// ABOUTME: Chat message component for investigation conversations.
// ABOUTME: Renders user and assistant messages with SDK content blocks.

import { memo } from 'react';
import { motion } from 'framer-motion';
import { User } from 'lucide-react';
import type { ContentBlock, ToolResultBlock, TextBlock, SmartAction } from '@/types/sdk';
import { AgentExecutionView } from './AgentExecutionView';

interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string | ContentBlock[];
  toolResults?: Map<string, ToolResultBlock>;
  messageId: string;
  onPin?: (messageId: string) => void;
  onUnpin?: (messageId: string) => void;
  isPinned?: boolean;
  showPinButton?: boolean;
  isStreaming?: boolean;
  currentPhase?: string;
  showSmartActions?: boolean;
  onSmartAction?: (action: SmartAction) => void;
  onSendFollowUp?: (message: string, clarificationAnswer?: boolean) => void;
  /** If a clarification in this message was already resolved, this is the user's selected label. */
  clarificationResolvedLabel?: string;
  /** If an ask_user_question in this message was already answered, this is the user's answer text. */
  askUserResolvedAnswer?: string;
  /** True when the conversation moved past this clarification without answering it (lock the card). */
  askUserSuperseded?: boolean;
}

// Memoized to prevent re-renders when parent state changes (e.g., input typing)
export const ChatMessage = memo(function ChatMessage({
  role,
  content,
  toolResults,
  messageId,
  onPin,
  onUnpin,
  isPinned = false,
  showPinButton = false,
  isStreaming = false,
  currentPhase,
  showSmartActions = false,
  onSmartAction,
  onSendFollowUp,
  clarificationResolvedLabel,
  askUserResolvedAnswer,
  askUserSuperseded,
}: ChatMessageProps) {
  if (role === 'user') {
    return <UserMessage content={content as string} />;
  }

  // Wrap callbacks to include messageId
  const handlePin = onPin ? () => onPin(messageId) : undefined;
  const handleUnpin = onUnpin ? () => onUnpin(messageId) : undefined;

  return (
    <AssistantMessage
      content={content as ContentBlock[]}
      toolResults={toolResults}
      onPin={handlePin}
      onUnpin={handleUnpin}
      isPinned={isPinned}
      showPinButton={showPinButton}
      isStreaming={isStreaming}
      currentPhase={currentPhase}
      showSmartActions={showSmartActions}
      onSmartAction={onSmartAction}
      onSendFollowUp={onSendFollowUp}
      clarificationResolvedLabel={clarificationResolvedLabel}
      askUserResolvedAnswer={askUserResolvedAnswer}
      askUserSuperseded={askUserSuperseded}
    />
  );
})

// User message bubble
function UserMessage({ content }: { content: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-start justify-end gap-3"
    >
      <div className="px-5 py-3 rounded-sm bg-primary-500 shadow-md max-w-2xl">
        <p className="text-base text-white font-medium leading-relaxed">{content}</p>
      </div>
      <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-primary-500 to-primary-400 p-[1px] shrink-0">
        <div className="w-full h-full rounded-full bg-surface-0 flex items-center justify-center">
          <User className="w-5 h-5 text-primary-600" />
        </div>
      </div>
    </motion.div>
  );
}

// Assistant message with content blocks
function AssistantMessage({
  content,
  toolResults,
  onPin,
  onUnpin,
  isPinned,
  showPinButton,
  isStreaming,
  currentPhase,
  showSmartActions,
  onSmartAction,
  onSendFollowUp,
  clarificationResolvedLabel,
  askUserResolvedAnswer,
  askUserSuperseded,
}: {
  content: ContentBlock[];
  toolResults?: Map<string, ToolResultBlock>;
  onPin?: () => void;
  onUnpin?: () => void;
  isPinned?: boolean;
  showPinButton?: boolean;
  isStreaming?: boolean;
  showSmartActions?: boolean;
  currentPhase?: string;
  onSmartAction?: (action: SmartAction) => void;
  onSendFollowUp?: (message: string, clarificationAnswer?: boolean) => void;
  clarificationResolvedLabel?: string;
  askUserResolvedAnswer?: string;
  askUserSuperseded?: boolean;
}) {
  // Ensure content is an array - may receive string during error states
  const contentArray = Array.isArray(content) ? content : [];

  // Check for tool_use blocks (used to show pin button and tool call details)
  const hasVisualContent = contentArray.some(block => block.type === 'tool_use');

  // Check if we have any rationale entries (tool_use with rationale field)
  const hasRationale = contentArray.some(
    block => block.type === 'tool_use' && (block as { input: { rationale?: string } }).input?.rationale
  );

  // Check for plain text content (shown as fallback)
  const hasPlainText = contentArray.some(block => {
    if (block.type === 'text') {
      const textBlock = block as TextBlock;
      const text = textBlock.text;
      return !!(text && text.trim().length > 0);
    }
    return false;
  });

  // Check for write_learning tool calls (rendered as collapsed summary by AgentExecutionView)
  const hasWriteLearning = contentArray.some(block => {
    if (block.type === 'tool_use') {
      const name = (block as { name: string }).name;
      const mcpMatch = name.match(/^mcp__[^_]+__(.+)$/);
      const normalized = mcpMatch ? mcpMatch[1] : name;
      return normalized === 'write_learning';
    }
    return false;
  });

  // Check for executed or compile-only Pure query tool calls.
  const hasPureQuery = contentArray.some(block => {
    if (block.type === 'tool_use') {
      const name = (block as { name: string }).name;
      const mcpMatch = name.match(/^mcp__[^_]+__(.+)$/);
      const normalized = mcpMatch ? mcpMatch[1] : name;
      return normalized === 'execute_pure_query' || normalized === 'compile_pure_query';
    }
    return false;
  });

  // Check for ask_user_question (SIR clarification) tool calls.
  const hasAskUser = contentArray.some(block => {
    if (block.type === 'tool_use') {
      const name = (block as { name: string }).name;
      const mcpMatch = name.match(/^mcp__[^_]+__(.+)$/);
      const normalized = mcpMatch ? mcpMatch[1] : name;
      return normalized === 'ask_user_question';
    }
    return false;
  });

  // During streaming: always show (AgentExecutionView handles loading state)
  // After complete: show if we have text, tool rationale, write learning, or pure query results
  const hasAnyContent = hasRationale || hasPlainText || hasWriteLearning || hasPureQuery || hasAskUser;
  const shouldRender = isStreaming || hasAnyContent;

  if (!shouldRender) {
    return null;
  }

  return (
    <div className="flex items-start gap-3 w-full">

      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-surface-100 flex items-center justify-center shrink-0 shadow-sm">
        <div className="w-5 h-5 bg-gradient-to-r from-primary-500 to-primary-400 rounded-full" />
      </div>

      {/* Message content */}
      <div
        className="flex-1 min-w-0 max-w-4xl"
        data-tour={isStreaming ? 'streaming-message' : undefined}
      >
        <AgentExecutionView
          content={contentArray}
          toolResults={toolResults || new Map()}
          isComplete={!isStreaming}
          currentPhase={currentPhase}
          onPin={onPin}
          onUnpin={onUnpin}
          isPinned={isPinned}
          showPinButton={showPinButton && hasVisualContent}
          showSmartActions={showSmartActions}
          onSmartAction={onSmartAction}
          onSendFollowUp={onSendFollowUp}
          clarificationResolvedLabel={clarificationResolvedLabel}
          askUserResolvedAnswer={askUserResolvedAnswer}
          askUserSuperseded={askUserSuperseded}
        />
      </div>
    </div>
  );
}

